import { type ApiEnvelope, api, unwrap } from "@/lib/api";

export type FlashSaleDiscountType = "PERCENT" | "FIXED" | "FIXED_PRICE";
export type DynamicFlashStatus =
	| "active"
	| "inactive"
	| "expired"
	| "depleted"
	| "scheduled";

export interface FlashSaleProduct {
	id: string;
	title: string;
	sku: string;
	basePrice: number | string;
	compareAtPrice: number | string | null;
}

export interface FlashSale {
	id: string;
	name: string;
	productId: string;
	product?: FlashSaleProduct;
	variantId: string | null;
	discountType: FlashSaleDiscountType;
	discountValue: number;
	flashPrice: number;
	startsAt: string;
	expiresAt: string;
	stockLimit: number;
	soldCount: number;
	priority: number;
	isActive: boolean;
	createdAt: string;
	updatedAt: string;
}

export interface FlashSaleKpis {
	activeCount: number;
	scheduledCount: number;
	expiredCount: number;
	inactiveCount: number;
	totalCount: number;
	totalSoldCount: number;
}

export interface FlashSalesFilter {
	search?: string;
	status?: DynamicFlashStatus;
	page?: number;
	limit?: number;
}

export interface PaginatedFlashSales {
	items: FlashSale[];
	total: number;
	page: number;
	limit: number;
	pages: number;
}

export interface CreateFlashSaleInput {
	name: string;
	productId: string;
	variantId?: string | null;
	discountType: FlashSaleDiscountType;
	discountValue: number;
	startsAt: string;
	expiresAt: string;
	stockLimit?: number;
	priority?: number;
	isActive?: boolean;
}

export type UpdateFlashSaleInput = Partial<CreateFlashSaleInput>;

const toQs = (filter: FlashSalesFilter): string => {
	const params = new URLSearchParams();
	if (filter.search) params.set("search", filter.search);
	if (filter.status) params.set("status", filter.status);
	if (filter.page) params.set("page", String(filter.page));
	if (filter.limit) params.set("limit", String(filter.limit));
	const str = params.toString();
	return str ? `?${str}` : "";
};

export const flashSalesApi = {
	list: (filter: FlashSalesFilter = {}) =>
		unwrap<PaginatedFlashSales>(
			api.get<ApiEnvelope<PaginatedFlashSales>>(
				`/admin/promotions/flash${toQs(filter)}`,
			),
		),

	getKpis: () =>
		unwrap<FlashSaleKpis>(
			api.get<ApiEnvelope<FlashSaleKpis>>("/admin/promotions/flash/kpi"),
		),

	get: (id: string) =>
		unwrap<FlashSale>(
			api.get<ApiEnvelope<FlashSale>>(`/admin/promotions/flash/${id}`),
		),

	create: (data: CreateFlashSaleInput) =>
		unwrap<FlashSale>(
			api.post<ApiEnvelope<FlashSale>>("/admin/promotions/flash", data),
		),

	update: (id: string, data: UpdateFlashSaleInput) =>
		unwrap<FlashSale>(
			api.patch<ApiEnvelope<FlashSale>>(`/admin/promotions/flash/${id}`, data),
		),

	toggleStatus: (id: string, isActive: boolean) =>
		unwrap<FlashSale>(
			api.patch<ApiEnvelope<FlashSale>>(
				`/admin/promotions/flash/${id}/status`,
				{
					isActive,
				},
			),
		),

	delete: (id: string) => api.delete(`/admin/promotions/flash/${id}`),
};
